import { useEffect } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import AuthService from '@/services/AuthService';
import { sanitizeRedirect } from '@/lib/auth-redirect';
import { logger } from '@/config/logging';

const authLogger = logger.getChild('auth').getChild('authGuard');
/**
 * Hook that redirects to /login if user is not authenticated
 *
 * Usage:
 * ```tsx
 * function ProtectedComponent() {
 *   useAuthGuard();
 *   // Component content here
 * }
 * ```
 */
export function useAuthGuard() {
  const navigate = useNavigate();
  const router = useRouter();

  useEffect(() => {
    authLogger.debug('effect');
    const authService = AuthService.getInstance();

    const checkAuth = () => {
      authLogger.debug('checking auth');
      const isNotAuthenticated = !authService.isAuthenticated();
      const needsReAuthentication = authService.needsReAuthentication();
      // If user is not authenticated or needs re-authentication, redirect to login
      if (isNotAuthenticated || needsReAuthentication) {
        // Capture the deeplink so the login page can send the user back to it
        const redirect = sanitizeRedirect(router.state.location.href);
        authLogger.warn('Redirecting to login {state}', {
          state: { isNotAuthenticated, needsReAuthentication, redirect },
          // #3576: why (recorded by AuthService), and whether this is the
          // 100 ms initial check or a later auth-state change
          lastReauth: authService.getLastReauth(),
          msSinceLoad: Math.round(performance.now()),
          onLine: navigator.onLine,
        });
        navigate({
          to: '/login',
          search: redirect ? { redirect } : {},
          replace: true,
        });
      }
    };

    // Delay initial check slightly to allow login process to complete
    const timeoutId = setTimeout(checkAuth, 100);

    // Subscribe to auth state changes
    const unsubscribe = authService.subscribe(checkAuth);

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [navigate, router]);
}
